import { useEffect, useState } from "react";
import {
  Stack,
  Text,
  Paper,
  Group,
  Badge,
  Loader,
  Anchor,
  Breadcrumbs,
  NumberFormatter,
  Alert,
} from "@mantine/core";
import {
  IconArrowUp,
  IconArrowDown,
  IconClock,
  IconCoin,
} from "@tabler/icons-react";
import { getRequestHierarchy } from "../api";
import StatusBadge from "./StatusBadge";
import ProgressBadge from "./ProgressBadge";
import { RailSection } from "./RailSection";
import ReactTimeago from "react-timeago-i18n";

function RequestCard({ request, label, onClick }) {
  return (
    <Paper
      withBorder
      p="xs"
      radius="sm"
      style={{ cursor: "pointer" }}
      onClick={onClick}
    >
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={2}>
          <Group wrap="nowrap" gap="xs">
            {label && (
              <Badge size="xs" variant="light" color="gray">
                {label}
              </Badge>
            )}
            <Text size="sm" fw={500}>
              {request.agentRole || request.flowName || "Root"}
            </Text>
            <StatusBadge
              size="xs"
              status={request.status}
              warningCount={request.warningCount}
              cancelledAt={request.cancelledAt}
              cancelRequestedAt={request.cancelRequestedAt}
            />
            <ProgressBadge size="xs" score={request.progressScore} />
          </Group>
          <Text size="xs" c="dimmed">
            <ReactTimeago date={request.sent} />
          </Text>
        </Stack>
        <Stack gap={2} align="flex-end">
          <Group wrap="nowrap" gap={4}>
            <IconClock size={12} color="gray" />
            <Text size="xs" c="dimmed">
              <NumberFormatter
                value={request.milliseconds}
                thousandSeparator
                suffix="ms"
              />
            </Text>
          </Group>
          <Group wrap="nowrap" gap={4}>
            <IconCoin size={12} color="orange" />
            <Text size="xs" c="dimmed">
              <NumberFormatter
                suffix="¢"
                decimalScale={4}
                value={request.creditsUsed / 10000}
              />
            </Text>
          </Group>
        </Stack>
      </Group>
    </Paper>
  );
}

export default function RequestHierarchyPanel({ requestId, onNavigate }) {
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!requestId) return;

    setLoading(true);
    setError(null);

    getRequestHierarchy(requestId)
      .then((data) => {
        setHierarchy(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch hierarchy:", err);
        setError(err.message);
        setLoading(false);
      });
  }, [requestId]);

  if (loading) {
    return <Loader size="sm" />;
  }

  if (error) {
    return (
      <Alert color="red" size="sm">
        {error}
      </Alert>
    );
  }

  if (!hierarchy) {
    return null;
  }

  const isRoot = hierarchy.agentDepth === 0;
  const hasChildren = hierarchy.children?.length > 0;
  const hasParent = !!hierarchy.parent;

  // If this is a root request with no children, don't show the panel
  if (isRoot && !hasChildren) {
    return null;
  }

  // The run's own identity (role, depth) is the modal header's job — repeating it here was one of
  // the duplicate titles that made the rail read as a pile of unrelated cards.
  return (
    <Stack gap="lg">
      {hierarchy.breadcrumb?.length > 0 && (
        <RailSection label="Path">
          <Breadcrumbs
            separator="→"
            separatorMargin={4}
            styles={{ separator: { color: "var(--mantine-color-dimmed)" } }}
          >
            {hierarchy.breadcrumb.map((item) => (
              <Anchor
                key={item.id}
                size="xs"
                onClick={() => onNavigate?.(item.id)}
              >
                {item.agentRole || item.flowName || "Root"}
              </Anchor>
            ))}
            <Text size="xs" fw={600}>
              Current
            </Text>
          </Breadcrumbs>
        </RailSection>
      )}

      {hasParent && (
        <RailSection
          label="Parent"
          right={<IconArrowUp size={12} color="var(--mantine-color-dimmed)" />}
        >
          <RequestCard
            request={hierarchy.parent}
            onClick={() => onNavigate?.(hierarchy.parent.id)}
          />
        </RailSection>
      )}

      {hasChildren && (
        <RailSection
          label={`Sub-agents (${hierarchy.children.length})`}
          right={<IconArrowDown size={12} color="var(--mantine-color-dimmed)" />}
        >
          <Stack gap="xs">
            {hierarchy.children.map((child) => (
              <RequestCard
                key={child.id}
                request={child}
                onClick={() => onNavigate?.(child.id)}
              />
            ))}
          </Stack>
        </RailSection>
      )}
    </Stack>
  );
}
